import { useLocation, useNavigate } from "react-router";
import { SkyNavDrawer } from "@skypointcloud/skypoint-ui";
import { useMsalAuth } from "../../../hooks/useMsalAuth";
import { isEmployeeUserViewPath } from "../../../routes/employeePaths";
import {
  buildEmployeeNavSections,
  buildEmployeeUserNavSections,
  type EmployeeNavId,
} from "./employeeNavConfig";
import { useEmployeeAccess } from "../access/useEmployeeAccess";
import { useGuardedNavigation } from "../ui/LeaveGuard";
import { hasAnyBroadcastAccess } from "../../../../shared/employee/broadcastAccess";
import { REPORT_COUNT_CAP } from "../../../../shared/employee/moderation";
import {
  useGetEmployeeDueQuery,
  useGetEmployeeOpenMessageReportCountQuery,
  useGetEmployeeOpenReportCountQuery,
  useGetEmployeeResourcesOutstandingQuery,
  useGetEmployeeUnreadMessagesQuery,
} from "@/store/api/employeeApi";
import {
  getEmployeeNavRoute,
  isModuleNeutralEmployeeRoute,
  getSelectedEmployeeNavItem,
} from "../../dashboard/sidebar/routeMapping";

/**
 * Both report counts stop at REPORT_COUNT_CAP, so one at the cap means "that
 * many or more": the total is then pushed past it, for the badge's "99+".
 */
function reportTotal(content: number, messages: number): number {
  const total = content + messages;
  return content >= REPORT_COUNT_CAP || messages >= REPORT_COUNT_CAP
    ? Math.max(total, REPORT_COUNT_CAP + 1)
    : total;
}

interface EmployeeSidebarProps {
  collapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
}

/**
 * Left-nav for the skyEmployee module — same SkyNavDrawer chrome as the other
 * products. In the admin view items follow the caller's capabilities from
 * GET /employee/me; under /employee/my it is the employee-facing menu.
 */
export function EmployeeSidebar({ collapsed, onCollapseChange }: EmployeeSidebarProps) {
  const navigate = useNavigate();
  const guarded = useGuardedNavigation();
  const location = useLocation();
  const { getQueryString } = useMsalAuth();

  const access = useEmployeeAccess();
  const { can, me } = access;
  const messagesOn = access.messagesOn !== false;
  const userView = isEmployeeUserViewPath(location.pathname);
  // Policies waiting for my acknowledgement, for the Resources item (both views).
  const outstanding = useGetEmployeeResourcesOutstandingQuery();
  // Open surveys I haven't answered, for Surveys (acknowledgements count on Resources).
  const due = useGetEmployeeDueQuery();
  // Unread messages, for the Messages item in both views (#1346). It is a
  // cheap count sharing the conversations cache tag, so the socket's own
  // invalidations keep it current — the sidebar never polls for it.
  const unread = useGetEmployeeUnreadMessagesQuery(undefined, { skip: !messagesOn });
  // Open reports, for Reports in the admin view: each half only for those who
  // may act on it, so nobody is told of reports they can't open.
  const contentReports = useGetEmployeeOpenReportCountQuery(undefined, {
    skip: userView || !can("content.moderate"),
  });
  const messageReports = useGetEmployeeOpenMessageReportCountQuery(undefined, {
    skip: userView || !can("messages.reviewReports"),
  });
  const counts = {
    resourcesToAcknowledge: outstanding.data?.count,
    surveys: due.data?.filter((d) => d.kind === "survey").length,
    messages: unread.data?.count,
    reports: reportTotal(contentReports.data ?? 0, messageReports.data ?? 0),
  };
  const sections = userView
    ? buildEmployeeUserNavSections(counts, messagesOn)
    : buildEmployeeNavSections(can, counts, hasAnyBroadcastAccess(me?.broadcast), messagesOn);
  const selectedId = getSelectedEmployeeNavItem(location.pathname);

  const handleNavSelect = (itemId: string) => {
    const id = itemId as EmployeeNavId;
    const qs = getQueryString();
    // A shared page (Users & access) keeps the Employee shell via from=employee.
    const from = isModuleNeutralEmployeeRoute(id) ? `${qs ? "&" : "?"}from=employee` : "";
    const go = () => navigate(getEmployeeNavRoute(id) + qs + from);
    // A page with unsaved changes asks before it's left behind; the page
    // you're already on stays mounted, so there's nothing to lose.
    if (getEmployeeNavRoute(id) === location.pathname) go();
    else guarded(go);
  };

  return (
    <SkyNavDrawer
      className="sky-nav"
      sections={sections}
      selectedId={selectedId}
      onSelect={handleNavSelect}
      size="small"
      collapsible
      onCollapseChange={onCollapseChange}
      {...(collapsed !== undefined ? { collapsed } : {})}
    />
  );
}
